/** One in-flight save per slide; an ambiguous failure retains the exact request for retry. */
export function createSerialSaveQueue<T>() {
  const pending = new Map<string, T>();
  const retrying = new Map<string, T>();
  const running = new Map<string, Promise<void>>();

  return {
    enqueue(key: string, value: T) {
      pending.set(key, value);
    },
    has(key: string) {
      return pending.has(key) || retrying.has(key) || running.has(key);
    },
    discard(key: string) {
      pending.delete(key);
      retrying.delete(key);
    },
    clear() {
      // An already sent request may finish, but stale queued bodies must not be sent later.
      pending.clear();
      retrying.clear();
    },
    async flush(key: string, save: (value: T) => Promise<void>) {
      while (running.has(key)) await running.get(key);
      if (!retrying.has(key) && !pending.has(key)) return;
      // Defer execution until the promise has been registered, including synchronous failures.
      const work = Promise.resolve().then(async () => {
        while (retrying.has(key) || pending.has(key)) {
          const value = retrying.get(key) ?? pending.get(key)!;
          if (!retrying.has(key)) pending.delete(key);
          retrying.set(key, value);
          await save(value);
          retrying.delete(key);
        }
      });
      running.set(key, work);
      try {
        await work;
      } finally {
        if (running.get(key) === work) running.delete(key);
      }
    },
  };
}
